import { useContentMotion } from "../lib/useContentMotion";
import { useCallback, useRef, useState } from "react";
import type { StudioWorkspaceState } from "../lib/studioNavigation";
import { LoadingState } from "../shell/ui";
import { useAgentFrame, type AgentFrameMessage } from "./agentFrame";
import type { AgentInstance } from "./api";

/** One retained document per visited Agent; business state never moves between frames. */
export function StudioWorkspaces({ agents, current, hash, onState }: {
  agents: AgentInstance[]; current: string | null; hash: string;
  onState: (id: string, state: StudioWorkspaceState) => void;
}) {
  const [visited, setVisited] = useState<string[]>([]);
  const available = visited.filter(id => agents.some(agent => agent.id === id));
  const opened = current && agents.some(agent => agent.id === current) && !available.includes(current) ? [...available, current] : available;
  // Remembered in the render that opens it, not in an effect after it: a
  // navigation that lands before that effect runs would render without it and
  // take the frame down, and the Agent's page would load again from the start.
  if (opened.join() !== visited.join()) setVisited(opened);
  return <div className="studio-workspaces" hidden={!current}>
    {opened.map(id => <StudioWorkspace key={id} agent={agents.find(agent => agent.id === id)!}
      active={id === current} hash={id === current ? hash || "#/today" : undefined} onState={onState} />)}
  </div>;
}

function StudioWorkspace({ agent, active, hash, onState }: {
  agent: AgentInstance; active: boolean; hash?: string;
  onState: (id: string, state: StudioWorkspaceState) => void;
}) {
  const [src] = useState(() => `/agents/${encodeURIComponent(agent.id)}/?workspace=studio${hash || "#/today"}`);
  const [ready, setReady] = useState(false);
  const heard = useCallback((data: AgentFrameMessage) => {
    const state = data.state as StudioWorkspaceState | undefined;
    if (data.type !== "pa.studio.state" || !state || !Array.isArray(state.panels)) return;
    setReady(state.ready === true);
    onState(agent.id, state);
  }, [agent.id, onState]);
  const region = useRef<HTMLElement>(null);
  useContentMotion(region, agent.id, active && ready);
  const frame = useAgentFrame({ visible: active, route: hash, agent, onMessage: heard });
  return <section ref={region} className="studio-workspace" hidden={!active} aria-label={`${agent.name} 的工作区`}>
    {!ready ? <div className="studio-workspace-loading" role="status"><LoadingState label={`正在打开 ${agent.name}`} /></div> : null}
    <iframe ref={frame.ref} title={`${agent.name} 的工作区`} src={src} aria-busy={!ready} onLoad={frame.onLoad} />
  </section>;
}
